interface Waiter {
    resolve: (r: Response) => void;
    reject: (e: unknown) => void;
}

function getRequestKey(
    input: RequestInfo | URL,
    init?: RequestInit
): string | null {
    const url =
        typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.href
              : (input as Request).url;
    const method = init?.method ?? 'GET';
    if (method !== 'GET' && method !== 'HEAD') return null;
    return `${method}:${url}`;
}

/**
 * Wraps fetch to coalesce concurrent identical GET/HEAD requests.
 * Multiple callers requesting the same URL share one network call; each gets a cloned response.
 * Disabled on web (causes white page in bundled environment).
 */
export function createCoalescingFetch(innerFetch: typeof fetch): typeof fetch {
    const inFlight = new Map<string, Waiter[]>();

    return (
        input: RequestInfo | URL,
        init?: RequestInit
    ): Promise<Response> => {
        const key = getRequestKey(input, init);
        if (!key) return innerFetch(input, init);

        const existing = inFlight.get(key);
        if (existing) {
            return new Promise<Response>((resolve, reject) => {
                existing.push({ resolve, reject });
            });
        }

        const waiters: Waiter[] = [];
        inFlight.set(key, waiters);

        const promise = innerFetch(input, init);
        promise
            .then(response => {
                const toNotify = [...waiters];
                inFlight.delete(key);
                toNotify.forEach(({ resolve }) => resolve(response.clone()));
            })
            .catch(err => {
                const toNotify = [...waiters];
                inFlight.delete(key);
                toNotify.forEach(({ reject }) => reject(err));
            });

        return new Promise<Response>((resolve, reject) => {
            waiters.push({ resolve, reject });
        });
    };
}
